import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  computed,
  createEnvironmentInjector,
  EnvironmentInjector,
  ErrorHandler,
  inject,
  Injectable,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, finalize, map, Observable, of, take } from 'rxjs';
import { SessionCheckFailedError } from '../auth-api/session-errors';
import { AuthStore } from '../store/auth-store.service';

export type SessionState = 'authenticated' | 'anonymous';

const SESSION_CHECK_ATTEMPTS = 2;

function statusOf(error: unknown): number {
  return error instanceof HttpErrorResponse ? error.status : 0;
}

/**
 * Settles whether there is a session before a guard decides anything.
 *
 * A stored user is trusted as is. Without one, `/me` answers through the
 * cookie: a user means a session, a 401 means none. Any other failure says
 * nothing about the session, so `/me` is asked once more before the visitor
 * is treated as logged out.
 *
 * The attempts are counted here, once for the whole app: the web shell runs
 * one guard on the parent route and another on the child, and desktop runs
 * both on one route at once. Each `/me` failure is counted and reported once,
 * whichever guard sees it first.
 */
@Injectable({ providedIn: 'root' })
export class SessionGateService {
  readonly #authStore = inject(AuthStore);
  readonly #errorHandler = inject(ErrorHandler);
  readonly #injector = inject(EnvironmentInjector);
  #countedError: unknown;
  #attempts = 0;

  wait(): Observable<SessionState> {
    const authStore = this.#authStore;

    if (authStore.isAuthenticated()) {
      return of('authenticated');
    }
    if (authStore.isUserResourceDestroyed()) {
      return of('anonymous');
    }

    authStore.setResourceActive(true);

    // toObservable's effect lives as long as its injector, and take(1) does
    // not stop it: a scope per wait lets it end with the wait.
    const scope = createEnvironmentInjector([], this.#injector);

    return toObservable(
      computed(() => ({
        user: authStore.user(),
        error: authStore.userResource.error(),
      })),
      { injector: scope },
    ).pipe(
      map(({ user, error }) => this.#settle(user, error)),
      filter((state): state is SessionState => state !== null),
      take(1),
      finalize(() => scope.destroy()),
    );
  }

  #settle(user: unknown, error: unknown): SessionState | null {
    if (user) {
      this.#attempts = 0;
      return 'authenticated';
    }
    if (!error) {
      return null;
    }
    if (error === this.#countedError) {
      const gaveUp =
        statusOf(error) === HttpStatusCode.Unauthorized ||
        this.#attempts >= SESSION_CHECK_ATTEMPTS;
      return gaveUp ? 'anonymous' : null;
    }

    this.#countedError = error;
    this.#attempts += 1;

    if (statusOf(error) === HttpStatusCode.Unauthorized) {
      return 'anonymous';
    }
    if (this.#attempts < SESSION_CHECK_ATTEMPTS) {
      this.#authStore.reloadUser();
      return null;
    }
    this.#errorHandler.handleError(
      new SessionCheckFailedError(this.#attempts, statusOf(error)),
    );
    return 'anonymous';
  }
}
